import { PlaidVerificationStatus, pluralWords } from "@frec-js/common";

import {
  MoneyMovementSourceType,
  PlaidAccountStatus,
} from "../../generated/graphql";
import { TransferAccount } from "../../types/transferAccount";

enum TransferAccountValidationErrors {
  SystemPending = "Account in review.",
  VerificationPending = "Verify this bank account.",
}
export const transferAccountValidation = (a?: TransferAccount) => {
  if (!a || !a.status || a.type !== MoneyMovementSourceType.Ach) {
    return undefined;
  }
  if (a.status === PlaidAccountStatus.SystemPending) {
    return TransferAccountValidationErrors.SystemPending;
  }
  if (
    a.plaidAccount &&
    [
      PlaidVerificationStatus.PendingManualVerification,
      PlaidVerificationStatus.PendingAutomaticVerification,
      PlaidVerificationStatus.VerificationFailed,
    ].includes(a.plaidAccount.verificationStatus as PlaidVerificationStatus)
  ) {
    return TransferAccountValidationErrors.VerificationPending;
  }
};

type MoneyMovementEstimatedBusinessDaysAdditionalData = {
  // Is the amount you are transferring <= the cash in the sub account?
  amountCoveredBySubAccountCash?: boolean;
};

/**
 * Gives the estimated business days for a money movement from a source to a destination.
 *
 * [source][destination](additionalData?: MoneyMovementEstimatedBusinessDaysAdditionalData) => { lower: number; upper: number }
 */
const MONEY_MOVEMENT_ESTIMATED_BUSINESS_DAYS: Record<
  MoneyMovementSourceType,
  Record<
    MoneyMovementSourceType,
    (additionalData?: MoneyMovementEstimatedBusinessDaysAdditionalData) => {
      lower: number;
      upper: number;
    }
  >
> = {
  [MoneyMovementSourceType.Ach]: {
    [MoneyMovementSourceType.Ach]: () => ({ lower: 0, upper: 0 }), // not possible
    [MoneyMovementSourceType.Wire]: () => ({ lower: 0, upper: 0 }), // not possible
    [MoneyMovementSourceType.Check]: () => ({ lower: 0, upper: 0 }), // not possible
    [MoneyMovementSourceType.FrecCash]: () => ({ lower: 1, upper: 1 }),
    [MoneyMovementSourceType.Treasury]: () => ({ lower: 1, upper: 2 }),
    [MoneyMovementSourceType.DirectIndex]: () => ({ lower: 1, upper: 1 }),
    [MoneyMovementSourceType.LineOfCredit]: () => ({ lower: 1, upper: 1 }), // same as depositing to frec cash
    [MoneyMovementSourceType.Allocation]: () => ({ lower: 1, upper: 2 }),
  },
  [MoneyMovementSourceType.Wire]: {
    [MoneyMovementSourceType.Ach]: () => ({ lower: 0, upper: 0 }), // not possible
    [MoneyMovementSourceType.Wire]: () => ({ lower: 0, upper: 0 }), // not possible
    [MoneyMovementSourceType.Check]: () => ({ lower: 0, upper: 0 }), // not possible
    [MoneyMovementSourceType.FrecCash]: () => ({ lower: 1, upper: 1 }),
    [MoneyMovementSourceType.Treasury]: () => ({ lower: 1, upper: 2 }),
    [MoneyMovementSourceType.DirectIndex]: () => ({ lower: 1, upper: 1 }),
    [MoneyMovementSourceType.LineOfCredit]: () => ({ lower: 1, upper: 1 }), // same as frec cash to frec cash
    [MoneyMovementSourceType.Allocation]: () => ({ lower: 1, upper: 2 }),
  },
  // Unused
  [MoneyMovementSourceType.Check]: {
    [MoneyMovementSourceType.Ach]: () => ({ lower: 0, upper: 0 }),
    [MoneyMovementSourceType.Wire]: () => ({ lower: 0, upper: 0 }),
    [MoneyMovementSourceType.Check]: () => ({ lower: 0, upper: 0 }),
    [MoneyMovementSourceType.FrecCash]: () => ({ lower: 0, upper: 0 }),
    [MoneyMovementSourceType.Treasury]: () => ({ lower: 0, upper: 0 }),
    [MoneyMovementSourceType.DirectIndex]: () => ({ lower: 0, upper: 0 }),
    [MoneyMovementSourceType.LineOfCredit]: () => ({ lower: 0, upper: 0 }),
    [MoneyMovementSourceType.Allocation]: () => ({ lower: 0, upper: 0 }),
  },
  [MoneyMovementSourceType.FrecCash]: {
    [MoneyMovementSourceType.Ach]: () => ({ lower: 1, upper: 2 }),
    [MoneyMovementSourceType.Wire]: () => ({ lower: 1, upper: 2 }),
    [MoneyMovementSourceType.Check]: () => ({ lower: 0, upper: 0 }), // not possible
    [MoneyMovementSourceType.FrecCash]: () => ({ lower: 0, upper: 0 }), // not possible
    [MoneyMovementSourceType.Treasury]: () => ({ lower: 1, upper: 2 }),
    [MoneyMovementSourceType.DirectIndex]: () => ({ lower: 1, upper: 1 }),
    [MoneyMovementSourceType.LineOfCredit]: () => ({ lower: 0, upper: 0 }),
    [MoneyMovementSourceType.Allocation]: () => ({ lower: 1, upper: 2 }),
  },
  [MoneyMovementSourceType.Treasury]: {
    [MoneyMovementSourceType.Ach]: (d) =>
      d?.amountCoveredBySubAccountCash
        ? { lower: 1, upper: 2 }
        : { lower: 2, upper: 3 },
    [MoneyMovementSourceType.Wire]: () => ({ lower: 1, upper: 2 }),
    [MoneyMovementSourceType.Check]: () => ({ lower: 0, upper: 0 }), // not possible
    [MoneyMovementSourceType.FrecCash]: (d) =>
      d?.amountCoveredBySubAccountCash
        ? { lower: 0, upper: 0 }
        : { lower: 1, upper: 2 },
    [MoneyMovementSourceType.Treasury]: () => ({ lower: 0, upper: 0 }), // not possible
    [MoneyMovementSourceType.DirectIndex]: (d) =>
      d?.amountCoveredBySubAccountCash
        ? { lower: 1, upper: 1 }
        : { lower: 1, upper: 2 },
    [MoneyMovementSourceType.LineOfCredit]: (d) =>
      d?.amountCoveredBySubAccountCash
        ? { lower: 0, upper: 0 }
        : { lower: 1, upper: 2 },
    [MoneyMovementSourceType.Allocation]: () => ({ lower: 1, upper: 2 }),
  },
  [MoneyMovementSourceType.DirectIndex]: {
    [MoneyMovementSourceType.Ach]: (d) =>
      d?.amountCoveredBySubAccountCash
        ? { lower: 1, upper: 2 }
        : { lower: 1, upper: 3 },
    [MoneyMovementSourceType.Wire]: (d) =>
      d?.amountCoveredBySubAccountCash
        ? { lower: 1, upper: 2 }
        : { lower: 1, upper: 3 },
    [MoneyMovementSourceType.Check]: () => ({ lower: 0, upper: 0 }), // not possible
    [MoneyMovementSourceType.FrecCash]: (d) =>
      d?.amountCoveredBySubAccountCash
        ? { lower: 0, upper: 0 }
        : { lower: 1, upper: 1 },
    [MoneyMovementSourceType.Treasury]: () => ({ lower: 1, upper: 2 }),
    [MoneyMovementSourceType.DirectIndex]: () => ({ lower: 0, upper: 0 }), // not possible
    [MoneyMovementSourceType.LineOfCredit]: (d) =>
      d?.amountCoveredBySubAccountCash
        ? { lower: 0, upper: 0 }
        : { lower: 1, upper: 1 },
    [MoneyMovementSourceType.Allocation]: () => ({ lower: 1, upper: 2 }),
  },
  [MoneyMovementSourceType.LineOfCredit]: {
    [MoneyMovementSourceType.Ach]: () => ({ lower: 1, upper: 2 }),
    [MoneyMovementSourceType.Wire]: () => ({ lower: 1, upper: 2 }),
    [MoneyMovementSourceType.Check]: () => ({ lower: 0, upper: 0 }), // not possible
    [MoneyMovementSourceType.FrecCash]: () => ({ lower: 0, upper: 0 }),
    [MoneyMovementSourceType.Treasury]: () => ({ lower: 1, upper: 2 }),
    [MoneyMovementSourceType.DirectIndex]: () => ({ lower: 1, upper: 1 }),
    [MoneyMovementSourceType.LineOfCredit]: () => ({ lower: 0, upper: 0 }), // not possible
    [MoneyMovementSourceType.Allocation]: () => ({ lower: 1, upper: 2 }),
  },
  [MoneyMovementSourceType.Allocation]: {
    [MoneyMovementSourceType.Ach]: () => ({ lower: 2, upper: 3 }),
    [MoneyMovementSourceType.Wire]: () => ({ lower: 2, upper: 3 }),
    [MoneyMovementSourceType.Check]: () => ({ lower: 0, upper: 0 }), // not possible
    [MoneyMovementSourceType.FrecCash]: () => ({ lower: 1, upper: 2 }),
    [MoneyMovementSourceType.Treasury]: () => ({ lower: 2, upper: 3 }),
    [MoneyMovementSourceType.DirectIndex]: () => ({ lower: 1, upper: 2 }),
    [MoneyMovementSourceType.LineOfCredit]: () => ({ lower: 1, upper: 2 }),
    [MoneyMovementSourceType.Allocation]: () => ({ lower: 0, upper: 0 }), // not possible
  },
};

export const estimatedTimeFromSourceAndDestination = (
  source: MoneyMovementSourceType,
  destination: MoneyMovementSourceType,
  i18n = {
    instant: "Instant",
    businessDay: "business day",
    businessDay_plural: "business days",
  },
  additionalData?: MoneyMovementEstimatedBusinessDaysAdditionalData,
) => {
  const { lower, upper } =
    MONEY_MOVEMENT_ESTIMATED_BUSINESS_DAYS[source][destination](additionalData);
  if (lower === 0 || lower === upper) {
    if (upper === 0) {
      return i18n.instant;
    } else {
      return pluralWords(upper, i18n.businessDay, i18n.businessDay_plural);
    }
  } else {
    return `${lower}-${upper} ${i18n.businessDay_plural}`;
  }
};
